<template>
  <BaseModal
    is-open
    title="Welcome back"
    size="small"
    @close="close"
  >
    <div class="text-sm" v-text="topText" />

    <div class="flex gap-4 flex-col my-6">
      <FacebookLoginButton :api-config="apiConfig" :tracking-params="{...trackingParams, auth_method: 'facebook'}" text="Continue with Facebook" @click="onFacebookLoginClick" @success="onSocialSuccess" @error="setError" />
      <GoogleLoginButton :api-config="googleApiConfig" :tracking-params="{...trackingParams, auth_method: 'google'}" text="Continue with Google" @click="onGoogleLoginClick" @success="onSocialSuccess" @error="setError" />
    </div>

    <HorizontalLine text="or" class="my-5" />

    <EmailField
      v-model="form.email.value"
      :errors="form.email.errors"
      placeholder="Email address"
      @update:modelValue="form.email.errors = []"
    />
    <TextField
      v-model="form.password.value"
      :icon="LockIcon"
      :errors="form.password.errors"
      :is-marked-as-invalid="form.password.errors.length > 0"
      name="password"
      type="password"
      :maxlength="64"
      class="mb-1.5"
      placeholder="Password"
      @update:modelValue="form.password.errors = []"
    />
    <a target="_blank" :href="forgotPasswordUrl" class="font-semibold" @click="onForgotLinkClick">
      Forgot your password?
    </a>

    <BaseButton color="green" :is-loading="isLoading" class="w-full mt-4" @click="login">
      Log in
    </BaseButton>

    <div v-if="signUp" class="im:-mb-4 text-center">
      <HorizontalLine class="my-6" />
      Don't have an User Account? <a class="font-semibold cursor-pointer" @click="onSignUpClick">Sign up</a>
    </div>
  </BaseModal>

  <Captcha :config="captcha" @onTokenResponse="(token) => { captcha.token = token; }" />
</template>
<script>
import LockIcon from '@tagvenue/icons/24/line/security/lock-01.svg';
import { markRaw } from 'vue';

import EmailField from '@/components/Forms/EmailField.vue';
import BaseButton from '@/components/Ui/Buttons/BaseButton.vue';
import FacebookLoginButton from '@/components/Ui/Buttons/FacebookLoginButton.vue';
import GoogleLoginButton from '@/components/Ui/Buttons/GoogleLoginButton.vue';
import TextField from '@/components/Ui/Forms/TextField.vue';
import HorizontalLine from '@/components/Ui/HorizontalLine.vue';
import BaseModal from '@/components/Ui/Modals/BaseModal.vue';
import Captcha from '@/modules/public/enquiry-form/components/EnquiryForm/components/Captcha.vue';
import formValidation, { FORM_VALIDATION } from '@/services/formValidation';
import { csrfToken } from '@/utils';

export default {
  name: 'WelcomeBackLoginModal',
  emits: ['close', 'trackEvent', 'login', 'error', 'signUpClick'],
  mixins: [formValidation],
  components: {
    TextField,
    EmailField,
    FacebookLoginButton,
    GoogleLoginButton,
    BaseButton,
    Captcha,
    HorizontalLine,
    BaseModal,
  },
  props: {
    email: {
      type: String,
      required: false,
    },
    forgotPasswordUrl: {
      type: String,
      required: true,
    },
    signUp: {
      type: Boolean,
      default: false,
    },
    trackingParams: {
      type: Object,
      required: true,
    },
    apiConfig: {
      type: Object,
      required: true,
    },
    googleApiConfig: {
      type: Object,
      required: true,
    },
    topText: {
      type: String,
    },
  },
  data() {
    return {
      isLoading: false,
      LockIcon: markRaw(LockIcon),
      form: {
        email: {
          value: this.email || '',
          errors: [],
        },
        password: {
          value: '',
          errors: [],
        },
      },
      captcha: {
        sitekey: this.apiConfig?.captcha.sitekey,
        isEnabled: false,
        token: null,
      },
      formValidation: {
        email: {
          [FORM_VALIDATION.REQUIRED]: 'Please enter your email address',
          [FORM_VALIDATION.EMAIL]: 'Please enter a valid email address',
        },
        password: {
          [FORM_VALIDATION.REQUIRED]: 'Please enter your password',
        },
      },
    };
  },
  methods: {
    onSocialSuccess(response) {
      this.isLoading = true;

      this.$emit('login', { emailMatch: this.email === response?.email });
    },
    onForgotLinkClick() {
      this.trackEvent('forgot_password_link');
    },
    onFacebookLoginClick() {
      this.trackEvent('continue_with_facebook_button');
    },
    onGoogleLoginClick() {
      this.trackEvent('continue_with_google_button');
    },
    trackEvent(element, interaction = 'click') {
      this.$emit('trackEvent', { section: 'login_modal', element, interaction });
    },
    close() {
      this.isLoading = false;
      this.trackEvent('close_button');
      this.$emit('close');
    },
    setError(error) {
      this.$emit('error', error);
      this.isLoading = false;
    },
    login({ captcha = false } = {}) {
      const self = this;

      this.trackEvent('login_button');

      if (!this.validateFields()) {
        return;
      }

      this.isLoading = true;
      $.ajax({
        url: '/login/client',
        type: 'POST',
        hideDefaultErrorMessage: true,
        data: {
          'g-recaptcha-response': this.captcha.token,
          _token: csrfToken(),
          email: this.form.email.value,
          password: this.form.password.value,
          trackingParams: this.trackingParams,
        },
        success(data, type, xhr) {
          this.isLoading = false;

          if (xhr.status === 200) {
            self.$emit('login', { emailMatch: self.email === self.form.email.value });
          } else {
            self.setError('An error occurred. Sorry for the inconvenience.');
          }
        },
        error(response) {
          if (!captcha && response.responseJSON?.['g-recaptcha-response']) {
            self.captcha.token = null;
            self.captcha.isEnabled = true;
            console.log('Captcha requested');
            Rollbar.debug('Captcha requested');

            const unwatch = self.$watch('captcha.token', () => {
              self.isLoading = false;
              self.captcha.isEnabled = false;
              self.login({ captcha: true });
              unwatch();
            });

            return;
          } if (response.responseJSON.password) {
            self.form.password.errors = response.responseJSON.password;
          } else if (response.responseJSON.email) {
            self.form.email.errors = response.responseJSON.email;
          } else if (response.responseJSON.message) {
            self.setError(response.responseJSON.message);
          } else {
            self.setError('An error occurred. Sorry for the inconvenience.');
          }

          self.isLoading = false;
        },
      });
    },
    onSignUpClick() {
      this.$emit('signUpClick', this.form.email.value);
    },
  },
  mounted() {
    this.trackEvent('modal', 'visible');
  },
};
</script>
